import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Clock, CheckCircle2, PauseCircle, AlertTriangle, Truck, DollarSign, Fuel, Circle, TrendingUp } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import api from '../../services/api';
import { PageHeader } from '../../components/PageParts';
import { formatCurrency } from '../../utils/format';

const COLORS = ['#C52429', '#3B82F6', '#F59E0B', '#10B981', '#8B5CF6', '#EF4444', '#6B7280', '#EC4899'];

function Card({ icon: Icon, label, value, sub, tone = 'default', testid, onClick }) {
  const toneMap = {
    default: 'text-brand', warn: 'text-amber-600', critical: 'text-red-600', success: 'text-emerald-600',
  };
  return (
    <button data-testid={testid} onClick={onClick} disabled={!onClick} className="text-left rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 disabled:cursor-default card-hover w-full">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500 font-medium">{label}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
        </div>
        <div className={`h-10 w-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center ${toneMap[tone]}`}><Icon className="h-5 w-5" /></div>
      </div>
      {sub && <p className="text-xs text-neutral-500">{sub}</p>}
    </button>
  );
}

export default function MaintenanceDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, a] = await Promise.all([
          api.get('/maintenance-dashboard/stats'),
          api.get('/maintenance-dashboard/charts'),
          api.get('/maintenance-dashboard/notifications'),
        ]);
        setStats(s.data); setCharts(c.data); setAlerts(a.data || []);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !stats) {
    return (<div><PageHeader title="Dashboard da Manutenção" /><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-32 rounded-xl shimmer" />)}</div></div>);
  }

  return (
    <div>
      <PageHeader title="Dashboard da Manutenção" subtitle="Visão geral operacional e financeira" />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        <Card testid="kpi-os-open" icon={Wrench} label="OS Abertas" value={stats.os_open} onClick={() => navigate('/manutencao/os?status=Aberta')} />
        <Card testid="kpi-os-progress" icon={Clock} label="Em Andamento" value={stats.os_in_progress} tone="warn" onClick={() => navigate('/manutencao/os?status=Em Andamento')} />
        <Card testid="kpi-os-finished" icon={CheckCircle2} label="Finalizadas" value={stats.os_finished} tone="success" />
        <Card testid="kpi-os-waiting" icon={PauseCircle} label="Aguardando" value={stats.os_waiting} />
        <Card testid="kpi-preventive-overdue" icon={AlertTriangle} label="Preventivas Vencidas" value={stats.preventive_overdue} tone="critical" onClick={() => navigate('/manutencao/preventivas')} />
        <Card testid="kpi-vehicles-down" icon={Truck} label="Veículos Parados" value={stats.vehicles_down} tone="warn" />
        <Card testid="kpi-total-cost" icon={DollarSign} label="Custo Total" value={formatCurrency(stats.total_cost)} sub={`Média: ${formatCurrency(stats.avg_cost_per_vehicle)}/veíc.`} />
        <Card testid="kpi-tires-alert" icon={Circle} label="Pneus em Alerta" value={stats.tires_alert} tone="critical" onClick={() => navigate('/manutencao/pneus?alert=true')} />
      </div>

      {alerts.length > 0 && (
        <div data-testid="alerts-panel" className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Notificações ativas</h3>
          <ul className="space-y-1.5">
            {alerts.slice(0, 10).map((a, i) => (
              <li key={i} className="text-sm flex items-start gap-2 py-1.5 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${a.level === 'critical' ? 'bg-red-500' : a.level === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                <span>{a.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <h3 className="font-semibold mb-1">Custo por veículo</h3>
          <p className="text-xs text-neutral-500 mb-3">Top 10</p>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={charts.cost_per_vehicle}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => formatCurrency(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" fill="#C52429" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <h3 className="font-semibold mb-1">Custo por tipo de manutenção</h3>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={charts.cost_per_type} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={{ fontSize: 11 }}>
                  {charts.cost_per_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => formatCurrency(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <h3 className="font-semibold mb-1 flex items-center gap-2"><TrendingUp className="h-4 w-4" />OS por mês</h3>
          <p className="text-xs text-neutral-500 mb-3">Últimos 6 meses</p>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={charts.os_per_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" fill="#3B82F6" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <h3 className="font-semibold mb-1 flex items-center gap-2"><Fuel className="h-4 w-4" />Consumo médio (km/L)</h3>
          <p className="text-xs text-neutral-500 mb-3">Top 10 veículos</p>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={charts.fuel_consumption}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={v => `${v} km/L`} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" fill="#10B981" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
